import {
    Component,
    input,
    OnDestroy,
    OnInit,
    signal,
    ViewEncapsulation,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { ButtonComponent } from "~app/common/components/button/button.component";
import { RadiobuttonGroupComponent } from "~app/common/components/radiobuttongroup/radiobuttongroup.component";
import { AtgardNaturvardesbedomningDTO, NaturvardesbedomningKalla } from "../../../models/naturvardesbedomning-models";
import { NvbLeveransvirkeAtgardsTyp, NvbLeveransvirkeFormGroup, NvbLeveransvirkeFormStep } from "./atgard-nvb-leveransvirke.model";

@Component({
    selector: "rask-atgard-nvb-leveransvirke",
    imports: [
        RadiobuttonGroupComponent,
        ReactiveFormsModule,
        FormsModule,
        ButtonComponent
    ],
    templateUrl: "./atgard-nvb-leveransvirke.component.html",
    styleUrl: "./atgard-nvb-leveransvirke.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class AtgardNvbLeveransvirkeComponent implements OnInit, OnDestroy {

    naturvardesbedomning = input<AtgardNaturvardesbedomningDTO>();
    isReadOnly = input<boolean>(false);
    protected form: FormGroup<NvbLeveransvirkeFormGroup>;
    protected NvbLeveransvirkeFormStep = NvbLeveransvirkeFormStep;
    protected readonly currentFormStep = signal<NvbLeveransvirkeFormStep>(NvbLeveransvirkeFormStep.MisstankeOmHogaNaturvarden);
    private readonly nextFormStep = signal<NvbLeveransvirkeFormStep>(NvbLeveransvirkeFormStep.MisstankeOmHogaNaturvarden);
    private readonly destroy$ = new Subject<void>();

    ngOnInit() {
        this.initForm();
        this.registerFormSubscriptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected continue() {
        const nextStep = this.nextFormStep();
        if (nextStep === NvbLeveransvirkeFormStep.NvbFormular || nextStep === NvbLeveransvirkeFormStep.NvbFil) {
            this.naturvardesbedomning().naturvardesbedomningKalla = this.form.controls.formOrFileNvb.value;
        } else if (nextStep === NvbLeveransvirkeFormStep.ForenkladRiskbedomning || nextStep === NvbLeveransvirkeFormStep.ForenkladRiskbedomningFil) {
            this.naturvardesbedomning().naturvardesbedomningKalla = this.form.controls.formOrFileRiskbedomning.value;
        } else {
            this.currentFormStep.set(this.nextFormStep());
        }

    }

    protected goBack() {
        this.currentFormStep.set(NvbLeveransvirkeFormStep.MisstankeOmHogaNaturvarden);
        this.nextFormStep.set(NvbLeveransvirkeFormStep.MisstankeOmHogaNaturvarden);
        this.form.reset();
    }

    protected isValid(): boolean {
        if (this.currentFormStep() === NvbLeveransvirkeFormStep.MisstankeOmHogaNaturvarden) {
            return this.form.controls.misstankeOmHogaNaturvarden.valid;
        } else if (this.currentFormStep() === NvbLeveransvirkeFormStep.AtgardsTyp) {
            return this.form.controls.atgardsTyp.valid;
        } else if (this.currentFormStep() === NvbLeveransvirkeFormStep.FormOrFileNvb) {
            return this.form.controls.formOrFileNvb.valid;
        } else if (this.currentFormStep() === NvbLeveransvirkeFormStep.FormOrFileRiskbedomning) {
            return this.form.controls.formOrFileRiskbedomning.valid;
        }
        return false;
    }

    private initForm() {
        this.form = new FormGroup<NvbLeveransvirkeFormGroup>({
            misstankeOmHogaNaturvarden: new FormControl<boolean>({
                value: null,
                disabled: this.isReadOnly()
            }, Validators.required),
            atgardsTyp: new FormControl<NvbLeveransvirkeAtgardsTyp>({
                value: null,
                disabled: this.isReadOnly()
            }, Validators.required),
            formOrFileNvb: new FormControl<NaturvardesbedomningKalla>({
                value: null,
                disabled: this.isReadOnly()
            }, Validators.required),
            formOrFileRiskbedomning: new FormControl<NaturvardesbedomningKalla>({
                value: null,
                disabled: this.isReadOnly()
            }, Validators.required)
        });
    }

    private registerFormSubscriptions() {
        this.form.controls.misstankeOmHogaNaturvarden.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) =>
                value ? this.nextFormStep.set(NvbLeveransvirkeFormStep.FormOrFileNvb)
                    : this.nextFormStep.set(NvbLeveransvirkeFormStep.AtgardsTyp));

        this.form.controls.atgardsTyp.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) =>
                value === NvbLeveransvirkeAtgardsTyp.Favv ?
                    this.nextFormStep.set(NvbLeveransvirkeFormStep.FormOrFileNvb)
                    : this.nextFormStep.set(NvbLeveransvirkeFormStep.FormOrFileRiskbedomning)
            );

        this.form.controls.formOrFileNvb.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) =>
                value === "Formulär" ?
                    this.nextFormStep.set(NvbLeveransvirkeFormStep.NvbFormular)
                    : this.nextFormStep.set(NvbLeveransvirkeFormStep.NvbFil));

        this.form.controls.formOrFileRiskbedomning.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) =>
                value === "Riskbedömning" ?
                    this.nextFormStep.set(NvbLeveransvirkeFormStep.ForenkladRiskbedomning)
                    : this.nextFormStep.set(NvbLeveransvirkeFormStep.ForenkladRiskbedomningFil)
            );
    }
}
